import { Injectable } from '@angular/core';
import { environment } from '@biletix/env';
import { I18nService } from '@biletix/core/services/i18n.service';

/**
 * Bir sayfanın kanonik yolunun parçaları.
 *
 * Rotalar `<segments>/:region/:language[/:slug]` biçiminde olduğu için region ve
 * dil parçalarını servis yerleştirir; çağıran yalnızca kimlik taşıyan segmentleri
 * ve varsa slug'ı verir.
 */
export interface SeoPagePath {
  /** Region/dil'den ÖNCE gelen segmentler, ör. `['etkinlik', 'K12345']`. */
  segments: string[];
  /** Region/dil'den SONRA gelen slug, ör. `'ajda-pekkan-konseri'`. */
  slug?: string;
  /**
   * `false` verilirse yola region eklenmez. `hediyekart/:language` gibi
   * region taşımayan rotalar için gerekli.
   */
  includeRegion?: boolean;
}

export interface SeoAlternateUrl {
  /** `hreflang` niteliğinin değeri: bir dil kodu ya da `x-default`. */
  hreflang: string;
  url: string;
}

/**
 * Kanonik URL'de kullanılan region.
 *
 * Uygulama aynı varlığı 6 region × 2 dil = 12 URL üzerinden sunuyor
 * (`core.module.ts` içindeki `USE_DEFAULT_REGIONS`). Region yalnızca listeleme
 * sayfalarındaki ön seçimi değiştiriyor, içeriğin kimliğini değiştirmiyor; bu
 * yüzden kanonik URL her zaman varsayılan region'a normalize edilir ve
 * kalan varyantlar tek bir URL'de toplanır.
 */
const CANONICAL_REGION = 'TURKIYE';

/** `x-default` işaret edilen dil — sitenin varsayılan dili. */
const X_DEFAULT_LANG = 'tr';

/**
 * Sayfaların kanonik ve alternatif (hreflang) URL'lerini üretir.
 *
 * Neden `RouterService` kullanılmıyor: oradaki `create*PageUrl` metotları
 * `baseHref` + O ANKİ region/dil ile yol üretir (`regionLanguagePath`). Kanonik
 * URL'in ise mutlak olması, region'ı normalize etmesi ve hreflang için başka bir
 * dilde de üretilebilmesi gerekir. `RouterService.getHostName()` de kullanılamaz:
 * SSR'da `WINDOW` mock'unun gerçek bir `location`'ı yok (`core/common.ts`).
 */
@Injectable({
  providedIn: 'root',
})
export class SeoUrlService {
  constructor(private i18n: I18nService) {}

  /**
   * Sayfanın kanonik mutlak URL'i.
   *
   * @param path Sayfanın yol parçaları.
   * @param lang Hedef dil; verilmezse o anki dil kullanılır.
   */
  buildCanonicalUrl(path: SeoPagePath, lang?: string): string {
    return this.buildUrl(path, lang ?? this.i18n.currentLang);
  }

  /**
   * Sayfanın tüm dil varyantları + `x-default`.
   *
   * Dil listesi `I18nService.getAvailableLocales()`'ten okunur, yani yeni bir dil
   * eklendiğinde hreflang seti kendiliğinden büyür.
   */
  buildAlternateUrls(path: SeoPagePath): SeoAlternateUrl[] {
    const alternates: SeoAlternateUrl[] = this.i18n
      .getAvailableLocales()
      .map((locale) => ({
        hreflang: locale.lang,
        url: this.buildUrl(path, locale.lang),
      }));

    alternates.push({
      hreflang: 'x-default',
      url: this.buildUrl(path, X_DEFAULT_LANG),
    });

    return alternates;
  }

  private buildUrl(path: SeoPagePath, lang: string): string {
    const segments = [...path.segments.map((segment) => this.clean(segment))];

    if (path.includeRegion !== false) {
      segments.push(CANONICAL_REGION);
    }

    segments.push(lang);

    const slug = path.slug ? this.clean(path.slug) : '';
    if (slug) {
      segments.push(slug);
    }

    const host = environment.biletixWebHost.replace(/\/+$/, '');
    return `${host}/${segments.filter((segment) => segment !== '').join('/')}`;
  }

  /** Baştaki/sondaki eğik çizgileri atar; boş segment üretmemek için. */
  private clean(segment: string): string {
    return `${segment ?? ''}`.trim().replace(/^\/+|\/+$/g, '');
  }
}
